import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { catchError, map, tap, switchMap, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { Permission, FeatureHierarchy } from '../graphql/generated/graphql';
import { MeRestApiService } from '../services/me/me-rest-api.service';
import { RealtimeService } from '../realtime/realtime.service';
import { RealtimeEvents } from '../realtime/realtime-events';
import { readStoredToken } from './auth-token';

export const STATIC_CREDENTIALS = {
  email: 'bilal123@4iisolutions.com',
  // email: 'wali@test.com',
  // email: 'nabeel@4iisolutions.com',
  // email: 'apr@mail.com',
  // email: 'javed.waqas@gmail.com',
  password: 'Test@123',
};


interface AccessToken {
  token: string;
  expires: string;
}

interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  logo?: string;
  avatar_location?: string;
  date_format?: string;
  currency_symbol?: string;
  currency_format?: string;
  isOwner?: boolean;
  user_is_super_admin?: boolean;
  business_is_super_admin?: boolean;
  is_popup_closed?: boolean;
  is_sample_data?: boolean;
  two_fa_secret?: string;
  business_id: {
    _id: string;
    logo?: string;
    business_legal_name?: string;
    business_type?: string;
    time_zone?: string;
    is_24_hour_format?: boolean;
    business_package?: { hr_module_only?: boolean } | null;
  } | null;
  business_status: string;
  business_type?: string;
}

interface LoginResult {
  tokens: {
    access: AccessToken;
    refresh: AccessToken;
  };
  user: AuthUser;
}

interface LoginResponse {
  errors?: Array<{ message: string }>;
  data: {
    loginWithEmail: LoginResult;
  };
}

interface LoginToOtherBusinessInput {
  business_id?: string;
  [key: string]: unknown;
}

interface LoginToOtherBusinessResponse {
  data: {
    loginToOtherBusiness: LoginResult;
  };
}

interface PermissionsResult {
  getLoggedInUserAllowModulesAndFeature: {
    modules?: string[] | null;
    features?: string[] | null;
    permission?: Permission[] | null;
    feature_hierarchy?: FeatureHierarchy[] | null;
  } | null;
}

interface LoggedInUserDetailData {
  is_navigated_user?: boolean | null;
  two_fa_secret?: string | null;
  last_working_date?: string | null;
  is_sample_data?: boolean | null;
  isOwner?: boolean | null;
  id?: string | null;
  profile_pictures?: Array<{ dimension?: string | null; path?: string | null }> | null;
  business_id?: {
    zip_code?: string | null;
    is_24_hour_format?: boolean | null;
    date_format?: string | null;
    time_zone?: string | null;
    currency_format?: string | null;
    currency_name?: string | null;
    Country?: { name?: string | null; _id?: string | null } | null;
    state?: { _id?: string | null; state_name?: string | null } | null;
    city?: { _id?: string | null; city_name?: string | null } | null;
    _id?: string | null;
    logo?: string | null;
    business_legal_name?: string | null;
    business_type?: string | null;
    business_package?: { hr_module_only?: boolean | null } | null;
  } | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  logo?: string | null;
  avatar_location?: string | null;
  date_format?: string | null;
  currency_symbol?: string | null;
  currency_format?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly apollo = inject(Apollo);
  private readonly meRestApi = inject(MeRestApiService);
  private readonly realtime = inject(RealtimeService);

  readonly allowedModules = signal<string[]>([]);
  readonly allowedFeatures = signal<string[]>([]);
  readonly allowedFeatureActions = signal<Permission[]>([]);
  readonly featureHierarchy = signal<FeatureHierarchy[]>([]);
  readonly permissionsLoaded = signal(false);
  private disturbKey = 'doNotDisturb';

  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  /** Single entry point for the API auth token. Reads via {@link readStoredToken}. */
  getToken(): string | null {
    return readStoredToken();
  }

  getUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem('user');
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }

  login(email: string, password: string): import('rxjs').Observable<LoginResponse> {
    const body = {
      query: `
        mutation loginWithEmail($email: String!, $password: String!) {
          loginWithEmail(email: $email, password: $password) {
             tokens {
      access {
        token
        expires
      }
      refresh {
        token
        expires
      }
    }
    user {
    is_navigated_user
    date_format
     currency_format
    currency_name
    currency_symbol
    do_not_disturb
    two_fa_secret
    is_un_restricted
    last_working_date
    is_sample_data
    isOwner
    department {
    _id
    department_name
   } 
      id
        profile_pictures {
        dimension
        path
      }
      business_id {
        zip_code
        is_24_hour_format
         date_format
      time_zone
      currency_format
      currency_name
        Country {
        name
        _id
      } 
      state {
        _id
        state_name
      }
      city {
        _id
        city_name
      }
        _id
         logo
      business_legal_name
        business_type
        time_zone
        business_package {
          hr_module_only
        }
      }
      business_status
      business_type
      first_name
      last_name
      email
      phone
      logo
      avatar_location
      user_is_super_admin
      is_popup_closed
      business_is_super_admin

    }
    rocketChat {
      userId
      authToken
      refreshToken
    }
    businesses {
        pictures {
        dimension
        path
      }
      business_type
      _id
      business_email
      business_legal_name
      address
      logo
      number_of_stores
      date_format
      time_zone
      Country {
        _id
        name
        short_name
      }
      status
      city {
        _id
        city_name
      }
    }
    
          }
        }
      `,
      variables: { email, password },
    };

    return this.http.post<LoginResponse>(environment.graphqlURI, body).pipe(
      tap(res => {
        if (res?.data?.loginWithEmail) {
          this.storeAuthData(res.data.loginWithEmail);
        }
      })
    );
  }

  loginToOtherBusiness(input: LoginToOtherBusinessInput): import('rxjs').Observable<LoginToOtherBusinessResponse> {
    const body = {
      query: `
        mutation LoginToOtherBusiness($input: loginToOtherBusinessInput) {
          loginToOtherBusiness(input: $input) {
            tokens {
              access {
                token
                expires
              }
              refresh {
                token
                expires
              }
            }
            user {
              is_navigated_user
              date_format
              currency_format
              currency_name
              currency_symbol
              do_not_disturb
              two_fa_secret
              is_un_restricted
              last_working_date
              is_sample_data
              isOwner
              id
              profile_pictures {
                dimension
                path
              }
              business_id {
                pictures {
                  dimension
                  path
                }
                zip_code
                Country {
                  name
                  _id
                }
                state {
                  _id
                  state_name
                }
                city {
                  _id
                  city_name
                }
                _id
                logo
                business_legal_name
                business_type
                time_zone
                business_package {
                  hr_module_only
                }
              }
              business_status
              business_type
              first_name
              last_name
              email
              phone
              logo
              avatar_location
              user_is_super_admin
              is_popup_closed
              business_is_super_admin
            }
            rocketChat {
              userId
              authToken
              refreshToken
            }
          }
        }
      `,
      variables: { input },
    };

    return this.http.post<LoginToOtherBusinessResponse>(environment.graphqlURI, body).pipe(
      tap(res => {
        if (res?.data?.loginToOtherBusiness) {
          this.storeAuthData(res.data.loginToOtherBusiness);
        }
      })
    );
  }

  storeAuthData(data: LoginResult): void {
    this.apollo.client.cache.reset();
    localStorage.setItem('access_token', JSON.stringify(data.tokens.access.token));
    localStorage.setItem('refresh_token', JSON.stringify(data.tokens.refresh.token));
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('dateformat', JSON.stringify(data.user.date_format ?? null));
    localStorage.setItem('currencySymbol', JSON.stringify(data.user.currency_symbol ?? null));
    localStorage.setItem('currencyFormat', JSON.stringify(data.user.currency_format ?? null));
    if (data.user?.business_id?._id) {
      localStorage.setItem('business', data.user.business_id._id);
      this.fetchSchedulerConfig(data.user.business_id._id);
    }
  }

  private fetchSchedulerConfig(businessId: string): void {
    // REST: GET /api/v2/roster-stores/:business/scheduler-config  (GQL getSchedulerConfig → REST)
    this.http
      .get<{ data?: unknown }>(`${environment.restV2URI}/api/v2/roster-stores/${businessId}/scheduler-config`)
      .subscribe({
        next: res => {
          const config = res?.data ?? null;
          if (config) {
            localStorage.setItem('schedulerConfig', JSON.stringify(config));
          }
        },
        error: () => { },
      });
  }

  public isActionAllowed(feature: string, action: string): boolean {
    if (this.allowedFeatureActions) {
      return this.allowedFeatureActions().findIndex(featureAction => featureAction.feature === feature && featureAction.permission === action) !== -1
    }
    return false
  }

  // REST: GET /api/v2/employees/me  (GQL getLoggedInUserDetail → REST)
  fetchAndStoreUserDetail(): Observable<void> {
    return this.meRestApi.getLoggedInUserDetail().pipe(
      tap(res => {
        const user = res as unknown as LoggedInUserDetailData | null;
        if (!user) return;
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('dateformat', JSON.stringify(user.date_format ?? null));
        localStorage.setItem('currencySymbol', JSON.stringify(user.currency_symbol ?? null));
        localStorage.setItem('currencyFormat', JSON.stringify(user.currency_format ?? null));
        if (user.business_id?._id) {
          localStorage.setItem('business', user.business_id._id);
          this.fetchSchedulerConfig(user.business_id._id);
        }
      }),
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }

  // REST: GET /api/v2/me/permissions  (GQL getLoggedInUserAllowModulesAndFeature → REST)
  loadPermissions(): import('rxjs').Observable<boolean> {
    return this.fetchAndStoreUserDetail().pipe(
      switchMap(() => this.meRestApi.getPermissions()),
      map((res) => {
        const data = res as PermissionsResult['getLoggedInUserAllowModulesAndFeature'];
        if (!data?.modules?.length) {
          this.allowedModules.set([]);
          this.allowedFeatures.set([]);
          this.allowedFeatureActions.set([]);
          this.featureHierarchy.set([]);
          this.permissionsLoaded.set(true);
          return false;
        }
        this.allowedModules.set((data.modules ?? []).filter(Boolean) as string[]);
        this.allowedFeatures.set((data.features ?? []).filter(Boolean) as string[]);
        this.allowedFeatureActions.set((data.permission ?? []).filter(Boolean) as Permission[]);
        this.featureHierarchy.set((data.feature_hierarchy ?? []).filter(Boolean) as FeatureHierarchy[]);
        this.permissionsLoaded.set(true);
        return true;
      }),
      catchError((err: unknown) => {
        console.log('[loadPermissions] error:', err);
        const e = err as { status?: number; error?: { message?: string }; message?: string };
        const netMsg = e?.error?.message ?? null;
        const msg = e?.message ?? netMsg;
        const ACCESS_DENIED = 'User does not has access to this location';
        if ((e?.status === 403 && netMsg === ACCESS_DENIED) || msg === ACCESS_DENIED) {
          window.location.href = '/dashboard';
          return of(false);
        }
        this.permissionsLoaded.set(true);
        return of(false);
      }),
    );
  }

  validatePermission(data: Record<string, string>): boolean {
    if (!data || Object.keys(data).length === 0) return true;

    if (data['feature']) {
      return this.allowedFeatures().includes(data['feature']);
    }

    if (data['permission']) {
      const user = this.getUser();
      if (user?.business_is_super_admin && data['permission'] === 'can_add_package_to_package_listing_list') {
        return true;
      }
      return this.allowedFeatureActions().some(p => p.permission === data['permission']);
    }

    if (data['menu']) {
      return this.allowedModules().includes(data['menu']);
    }

    return false;
  }

  clearPermissions(): void {
    this.allowedModules.set([]);
    this.allowedFeatures.set([]);
    this.allowedFeatureActions.set([]);
    this.featureHierarchy.set([]);
    this.permissionsLoaded.set(false);
  }

  seedDevAuth(): Observable<boolean> {
    if (this.isAuthenticated()) return of(true);
    return this.login(STATIC_CREDENTIALS.email, STATIC_CREDENTIALS.password).pipe(
      map(() => true),
      catchError(err => {
        console.error('[DEV] Auth seed failed', err);
        return of(false);
      }),
    );
  }

  signout(): void {
    const schedulerConfig = localStorage.getItem('schedulerConfig');
    this.clearPermissions();
    localStorage.clear();
    sessionStorage.clear();
    if (schedulerConfig) {
      localStorage.setItem('schedulerConfig', schedulerConfig);
    }
    this.router.navigateByUrl('/login');
  }
   setLocalStoreDisturb(data: any) {
    localStorage.setItem(this.disturbKey, JSON.stringify(data));
  }

  getlocalStoreDisturb(): any | null {
    return localStorage.getItem(this.disturbKey);
  }
  // ── Realtime subscriptions (WebSocket — replaces GraphQL subscriptions) ─────
  // These return the unwrapped event payload directly (no GraphQL `data` envelope).
  // user/business scopes are auto-subscribed, so the id args are no longer needed
  // (kept for call-site compatibility).

  usersPermissionsChangedNotify(
    _userId?: string,
  ): Observable<{ is_role_changed?: boolean; is_password_changed?: boolean; is_navigated_business_change?: boolean }> {
    return this.realtime
      .on<{ is_role_changed?: boolean; is_password_changed?: boolean; is_navigated_business_change?: boolean }>(
        RealtimeEvents.PERMISSIONS_CHANGED,
      )
      .pipe(map((event) => event.data));
  }

  usersInactiveNotify(_userId?: string): Observable<{ account_status?: boolean }> {
    return this.realtime
      .on<{ account_status?: boolean }>(RealtimeEvents.ACCOUNT_STATUS_CHANGED)
      .pipe(map((event) => event.data));
  }

  businessTimeZoneChange(
    _businessId?: string,
  ): Observable<{ is_country_change?: boolean; is_time_format_change?: boolean; is_time_zone_change?: boolean; is_date_format_change?: boolean }> {
    return this.realtime
      .on<{ is_country_change?: boolean; is_time_format_change?: boolean; is_time_zone_change?: boolean; is_date_format_change?: boolean }>(
        RealtimeEvents.BUSINESS_TIMEZONE_CHANGED,
      )
      .pipe(map((event) => event.data));
  }
}

